import { BaseAPI } from "@utils/base-api";
import {
  CUSTOMER_MANAGEMENT_CUSTOMER_UPDATE_CUSTOMER_PATH,
  CUSTOMER_MANAGEMENT_CUSTOMER_GET_CUSTOMER_PATH,
  ABANDONED_CART_PUBLIC_ENDPOINT_PATH,
} from "@./types/customer-management/endpoint-paths";
import {
  CreateOrUpdateAbandonedCartUserResponseDto,
} from "@./types/customer-management/dto/CreateOrUpdateAbandonedCartUserResponse.dto";
import {
  CreateOrUpdateAbandonedCartUserInternalParamsDto,
} from "@./types/customer-management/dto/CreateOrUpdateAbandonedCartUserInternalParams.dto";

import {
  ICreateOrUpdateAbandonedCartUserClientRequest,
  IUpdateCustomerDetailsRequest,
  IUpdateCustomerDetailsResponse,
  TGetUserResponse,
  TSimpleUserResponse,
} from "./types/customer.types";

export class CustomerManagementAPI extends BaseAPI {

  /**
   * Gets a user and their shipping and billing addresses via their UUID-based id
   */
  public async getFullUser(): Promise<TGetUserResponse> {
    const result = await this.axios.get(CUSTOMER_MANAGEMENT_CUSTOMER_GET_CUSTOMER_PATH);

    return result.data;
  }

  /**
   * Get a user's key information (name, email, phone number) via their UUID-based id.
   *
   * This is a simplified version of the full user response,
   * and is used for customer details pages.
   */
  public async getSimpleUser(): Promise<TSimpleUserResponse> {
    const user = await this.getFullUser();

    // A missing name is valid (e.g. express checkout can return a single-word name).
    // So we just log the error (don't throw) to prevent crashing the portal page,
    // and return empty strings so the fields render blank for the customer to complete.
    if (!user.firstName || !user.lastName) {
      console.warn("Customer is missing their first and/or last name.", { eventName: "getSimpleUserMissingName", userId: user.id });
    }

    return {
      id: user.id,
      email: user.email,
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      phoneNumber: user.phoneNumber ?? "",
    };
  }

  /**
   * Update the customer details for a given customer.
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the customer id
   * and the customer details
   * @returns The updated customer details.
   */
  public async updateCustomerDetails(request: IUpdateCustomerDetailsRequest): Promise<IUpdateCustomerDetailsResponse> {
    const result = await this.axios.put(
      CUSTOMER_MANAGEMENT_CUSTOMER_UPDATE_CUSTOMER_PATH,
      request,
    );

    return result.data;
  }

  /**
   * Create or update an abandoned cart user.
   * @param request The request body containing the customer details
   * @param params The parameters for the abandoned cart user
   * @returns {
   *   userId: string,
   *   legacyCustomerId: string,
   * }
   */

  public async createOrUpdateAbandonedCartUser(
    request: ICreateOrUpdateAbandonedCartUserClientRequest,
    params: CreateOrUpdateAbandonedCartUserInternalParamsDto,
  ): Promise<CreateOrUpdateAbandonedCartUserResponseDto> {
    try {
      const result = await this.axios.post(
        ABANDONED_CART_PUBLIC_ENDPOINT_PATH,
        request,
        {
          params,
        },
      );
      return result.data;
    } catch (error) {
      throw new Error("Error creating or updating abandoned cart user", { cause: error });
    }
  }

}
